import { useCallback, useEffect, useMemo } from 'react';
import { useModal } from 'mui-modal-provider';
import { enqueueSnackbar } from 'notistack';
import { Box } from '@mui/material';
import UIText from '@/components/text/UIText';
import UIGenericDialog from '@/components/expediente/modals/UIGenericDialog';
import { useBlockRequest } from '@/hooks/useBlockRequest';
import { BlockRequestRequest } from '@/services/block-request-registration/blockRequestService';
import { RowTable } from '@/components/table/UITableProps';
import { isSucursal, SPECIAL_USERS } from '@/utils/roles';
import { initializeLogCollector } from '@/utils/logCollector';
import { SessionInfo } from '@/context/currentSessionContext';

// ==================== TIPOS ====================
interface UseRequestControlConfig {
  // Estados válidos donde se permite tomar/soltar control
  validStatuses: string[];
  
  // Usuarios con permisos especiales (por defecto LBAEZA y RDIAZ)
  specialUsers?: string[];
  
  // Sesión del usuario actual
  session: SessionInfo;
  
  // Filtros aplicados (para Home - opcional para otros procesos)
  appliedFilters?: {
    aseguradora?: string;
    sucursal?: string;
    agenteId?: string[];
  };
  
  // Agentes disponibles (para Home - opcional para otros procesos)
  agents?: Array<{ id: string; name: string }>;
  
  // Contexto adicional (para emisión u otros procesos)
  additionalContext?: any;
  
  // Tipo de proceso (para diferenciar en el servicio)
  processType: 'REGISTRATION' | 'EMISSION' | 'FINANCIAL' | 'EXPEDIENT';
  
  // Callbacks después de tomar/soltar
  onTakeControl: (solicitud: RowTable) => void;
  onReleaseControl: (solicitud: RowTable) => void;
  
  // Estados de loading
  setIsLoading?: (loading: boolean) => void;
  setMessageLoading?: (message: string) => void;
  
  // Función alternativa para setState (para emisión)
  setState?: (state: any) => void;
  
  // Flag para debug
  debugMode?: boolean;
}

interface UseRequestControlReturn {
  takeControlHandler: (solicitud: RowTable) => void;
  releaseControlHandler: (solicitud: RowTable) => void;
  getTakeControlAction: () => any;
  getReleaseControlAction: () => any;
  isValidStatus: (idStatus: string | number, statuses?: string[]) => boolean;
}

// ==================== HOOK PRINCIPAL ====================
export const useRequestControl = (config: UseRequestControlConfig): UseRequestControlReturn => {
  const { showModal, hideModal } = useModal();
  const blockRequest = useBlockRequest();
  
  const {
    validStatuses,
    specialUsers = SPECIAL_USERS,
    session,
    appliedFilters,
    agents,
    additionalContext,
    processType = 'REGISTRATION',
    onTakeControl,
    onReleaseControl,
    setIsLoading,
    setMessageLoading,
    setState,
    debugMode = false
  } = config;

  useEffect(() => {
    initializeLogCollector();
  }, []);
  
  // ==================== UTILIDADES ====================
  
  // Determinar si es usuario especial
  const isSpecialUser = useMemo(() => 
    specialUsers.includes(session?.user?.login?.toUpperCase() || ''),
    [session?.user?.login, specialUsers]
  );
  
  // Determinar si es usuario sucursal
  const isUserSucursal = useMemo(() => {
    const role = session?.user?.role?.toUpperCase() || '';
    return isSucursal(role);
  }, [session?.user?.role]);
  
  // Validar si el estado permite control
  const isValidStatus = useCallback((idStatus: string | number, statuses?: string[]) => {
    if (!idStatus) return false;
    const id = idStatus.toString();
    if (!id || id === "") return false;
    const statusList = statuses || validStatuses;
    return statusList.includes(id);
  }, [validStatuses]);
  
  // ==================== HANDLERS ====================
  
  // Handler para mostrar error
  const handleErrorRequestControl = useCallback((takeControl: boolean) => {
    showModal(UIGenericDialog, {
      id: "modal-error-request-control",
      logo: "SAD",
      title: takeControl
        ? "No es posible tomar la solicitud"
        : "No es posible soltar la solicitud",
      message: (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <UIText
            text={
              takeControl
                ? "Ocurrió un error al tomar la solicitud. Por favor, refresca la página y asegúrate de que la solicitud esté disponible."
                : "Ocurrió un error al soltar la solicitud. Por favor, refresca la página y asegúrate de tener permisos sobre la solicitud."
            }
            color="blueGray"
            weight="light"
          />
        </Box>
      ),
      actions: [
        {
          label: "Aceptar",
          color: "orange",
          onClick: () => hideModal("modal-error-request-control"),
          fullWidth: true,
        }
      ],
      width: 460,
    });
  }, [showModal, hideModal]);
  
  // Handler principal para cambiar el control
  const changeControlHandler = useCallback(async (solicitud: RowTable, takeControl: boolean) => {
    try {
      // Manejo de loading según el proceso
      if (setState) {
        setState((prevState: any) => ({ 
          ...prevState, 
          isLoading: true, 
          messageLoading: "" 
        }));
      } else {
        setIsLoading?.(true);
        setMessageLoading?.("");
      }
      
      // Construir request base
      const request: BlockRequestRequest & { tipo?: string } = {
        idSolicitud: solicitud.idSolicitud?.toString() || solicitud.idProceso?.toString(),
        bloqueo: takeControl,
        idAgente: session?.user?.login ?? "",
        sucursal: isSucursal(session?.user?.role?.toUpperCase() || ''),
        tipo: processType
      };
      // Solo agregar estos campos cuando se TOMA control (bloqueo=true)
      if (takeControl) {
        request.correo = session?.user?.email ?? "";
        
        // Para proceso de ALTA (Home)
        if (processType === 'REGISTRATION' && appliedFilters) {
          // Obtener la subsidiary usando los filtros aplicados
          const subsidiary = session?.company
            ?.find((c: any) => c.name === appliedFilters.aseguradora)?.subsidiary
            .find((s: any) => `${s.subsidiary} - ${s.agentPositionName}` === appliedFilters.sucursal);
          if (subsidiary) {
            request.agenteIdSiebel = subsidiary.agentId;
          }

          const agent = agents?.find(a => a.id === (appliedFilters.agenteId ? appliedFilters.agenteId[0] : ''));
          if (agent && agent.id === solicitud.agenteCorredorId) {
            request.agenteCorredorId = agent.id;
            request.nombreAgente = agent.name;
          }else{
            console.warn("solicitud en request control:", solicitud, agent);
            request.agenteCorredorId = (solicitud.agenteCorredorId || "") as string ;
            request.nombreAgente = (solicitud.nombreAgente || "") as string;
          }
        }
        
        // Para proceso de EMISIÓN
        if ((processType === 'EMISSION' || processType === 'FINANCIAL' || processType === 'EXPEDIENT') && additionalContext?.data) {
          const emissionData = additionalContext.data;
          request.correo = session?.user?.email ?? "";
          request.agenteIdSiebel = emissionData.agent?.idAgenteAserta || "";
          request.agenteCorredorId = emissionData.agent?.idAgenteAserta ?? "";
          request.nombreAgente = emissionData.agent?.agenteName ?? "";
          request.idAgente = emissionData.agent?.idAgente || "";
          request.puestoIdAgente = emissionData.agent?.idPuestoAgente;
        }
      }
      
      if (debugMode) {
        console.log('Request to blockRequest service:', request);
      }
      request.nombreUsuario = session?.user?.raw?.[0].nombreUsuario ?? "";
      const response = await blockRequest(request);
      
      // Verificar si es un ErrorResponse
      if ('error' in response || 'statusCode' in response) {
        handleErrorRequestControl(takeControl);
        console.error("ErrorResponse en changeControlHandler:", response);
        return;
      }
      
      // Es un BlockRequestResponse exitoso
      if ("solicitudBloqueada" in response && response.solicitudBloqueada === takeControl) {
        if (!takeControl && !response.mensaje.includes("olicitud desbloqueada correctamente")) {
          handleErrorRequestControl(takeControl);
          console.error("No se pudo soltar el control:", response);
        } else {
          enqueueSnackbar(
            takeControl 
              ? "Has tomado control de la solicitud exitosamente" 
              : "Has soltado el control de la solicitud",
            { variant: "success", preventDuplicate: true }
          );
          
          if (takeControl) {
            onTakeControl(solicitud);
          } else {
            onReleaseControl(solicitud);
          }
        }
      } else {
        handleErrorRequestControl(takeControl);
        console.error("Respuesta inesperada en changeControlHandler:", response);
      }
    } catch (error: any) {
      handleErrorRequestControl(takeControl);
      console.error("Error en changeControlHandler:", error);
    } finally {
      // Manejo de loading según el proceso
      if (setState) {
        setState((prevState: any) => ({ 
          ...prevState, 
          isLoading: false, 
          messageLoading: "" 
        }));
      } else {
        setIsLoading?.(false);
      }
    }
  }, [
    session,
    appliedFilters,
    agents,
    additionalContext,
    processType,
    blockRequest,
    handleErrorRequestControl,
    onTakeControl,
    onReleaseControl,
    setIsLoading,
    setMessageLoading,
    setState,
    isUserSucursal,
    debugMode
  ]);
  
  // Handler para mostrar modal de confirmación
  const handleRequestControl = useCallback(async (
    solicitud: RowTable, 
    isTaking: boolean
  ) => {
    const config = {
      take: {
        title: "¿Deseas tomar control de la solicitud?",
        message: "Al tomar control de la solicitud te convertirás en el responsable del seguimiento y finalización de la misma.",
        actionLabel: "Tomar solicitud",
      },
      release: {
        title: "¿Deseas soltar el control de la solicitud?",
        message: "Estás a punto de soltar la solicitud, esto permitirá que un agente diferente pueda tomar control de la misma.",
        actionLabel: "Soltar solicitud",
      }
    };
    
    const currentConfig = isTaking ? config.take : config.release;
    
    try {
      showModal(UIGenericDialog, {
        id: "modal-request-control",
        logo: "SMILE",
        title: currentConfig.title,
        message: (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <UIText
              text={currentConfig.message}
              color="blueGray"
              weight="light"              
            />
          </Box>
        ),
        actions: [
          {
            label: "Cancelar",
            color: "secondary",
            onClick: () => hideModal("modal-request-control"),
            fullWidth: true,
          },
          {
            label: currentConfig.actionLabel,
            color: "orange",
            onClick: async () => {
              hideModal("modal-request-control");
              await changeControlHandler(solicitud, isTaking);
            },
            fullWidth: true,
          }
        ],
        width: 460,
      });
    } catch (error: any) {
      console.error(`Error ${isTaking ? 'taking' : 'releasing'} control:`, error);
    }
  }, [showModal, hideModal, changeControlHandler]);
  
  // ==================== HANDLERS FINALES ====================
  
  const takeControlHandler = useCallback((solicitud: RowTable) => {
    handleRequestControl(solicitud, true);
  }, [handleRequestControl]);
  
  const releaseControlHandler = useCallback((solicitud: RowTable) => {
    handleRequestControl(solicitud, false);
  }, [handleRequestControl]);
  
  // ==================== ACCIONES ====================
  
  // Función para obtener la acción de tomar control - VERSIÓN CORREGIDA
  const getTakeControlAction = useCallback(() => ({
    label: "Tomar control de la solicitud",
    onClick: takeControlHandler,
    icon: "/assets/icons/take.svg",
    disabled: (solicitud: RowTable) => {
      const canEdit = isValidStatus(solicitud.idEstatus);
      const isBlocked = solicitud.bloqueo.toString() === "true";
      
      // Si es especial o sucursal, verificar que tenga agente seleccionado (solo para ALTA)
      if (processType === 'REGISTRATION') {
        const requiresAgent = isSpecialUser || isUserSucursal;
        const hasAgentSelected = appliedFilters?.agenteId && appliedFilters.agenteId.length > 0;
        const agentValidation = requiresAgent ? !hasAgentSelected : false;
        
        return !canEdit || isBlocked || agentValidation;
      }
      
      return !canEdit || isBlocked;
    },
    show: (solicitud: RowTable) => {
      const canEdit = isValidStatus(solicitud.idEstatus);
      const isBlocked = solicitud.bloqueo.toString() === "true";
      
      const shouldShow = canEdit && !isBlocked;
      return shouldShow;
    }
  }), [
    takeControlHandler,
    isValidStatus,
    processType,
    isSpecialUser,
    isUserSucursal,
    appliedFilters,
    session,
    debugMode
  ]);
  
  // Función para obtener la acción de soltar control
  const getReleaseControlAction = useCallback(() => ({
    label: "Soltar control de la solicitud",
    onClick: releaseControlHandler,
    icon: "/assets/icons/take.svg",
    disabled: (solicitud: RowTable) => {
      const canEdit = isValidStatus(solicitud.idEstatus);
      const isBlocked = solicitud.bloqueo.toString() === "true";
      
      return !canEdit || !isBlocked;
    },
    show: (solicitud: RowTable) => {
      const canEdit = isValidStatus(solicitud.idEstatus);
      const isBlocked = solicitud.bloqueo.toString() === "true" ;
      const isMine = solicitud.nombreUsuario === session?.user?.login;
      const role = session?.user?.role?.toUpperCase();
      
      // Mostrar si está bloqueada Y (es mía O soy especial/sucursal)
      const shouldShow = canEdit && isBlocked && (isMine || isSpecialUser || isUserSucursal);
      return shouldShow;
    }
  }), [
    releaseControlHandler,
    isValidStatus,
    session?.user?.login,
    session?.user?.role,
    isSpecialUser,
    isUserSucursal,
    debugMode
  ]);
  
  return {
    takeControlHandler,
    releaseControlHandler,
    getTakeControlAction,
    getReleaseControlAction,
    isValidStatus
  };
};